import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './Button';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Logged for dev visibility — Sentry hook can be wired here later (PRD §17).
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="container-q section-q">
        <div className="glass-card glass-card-strong mx-auto max-w-xl p-8">
          <p className="text-label-mono text-[color:var(--color-error)]">Error</p>
          <h1 className="mt-4 font-[family-name:var(--font-editorial)] text-3xl font-semibold text-white">
            Something broke unexpectedly.
          </h1>
          <p className="mt-3 text-[color:var(--color-text-secondary)]">
            Try refreshing this section. If it persists, please reach out — we'd love to fix it.
          </p>
          <pre className="mt-6 max-h-32 overflow-auto rounded-md bg-black/40 p-3 text-xs text-[color:var(--color-text-secondary)]">
            {error.message}
          </pre>
          <div className="mt-6 flex gap-3">
            <Button onClick={this.reset}>Try again</Button>
            <Button variant="ghost" onClick={() => window.location.reload()}>
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
